import { useState, type FormEvent } from "react"
import { useNavigate, useLocation } from "react-router"
import { PASSWORD_POLICY_RULES, evaluatePasswordPolicy } from "@mc/types"
import { useAuth } from "../auth/AuthProvider.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { friendlyError } from "../api/errorMessages.js"
import { checkUserExists } from "../api/auth.js"

type Step = "username" | "login" | "register"

/**
 * Unified username-first auth screen (replaces the old separate
 * LoginPage.tsx/RegisterPage.tsx, moved to _deprecated/). One component,
 * one route render — the layout below just changes what it shows based
 * on `step`, it never mounts a different page. Flow: ask for a username
 * first, check GET /api/v1/auth/user-exists (checkUserExists), then
 * reveal a password field (login) if it exists or the create-account
 * form with the live password-policy checklist (register) if it
 * doesn't. `/login` and `/register` are both routed here (see App.tsx)
 * so existing bookmarks/links to either path keep working; the initial
 * step is always "username" regardless of which path was used, since
 * neither the frontend nor the visitor actually knows yet which of the
 * two flows applies.
 *
 * Not Supabase Auth: this project's login/register/session is its own
 * JWT + httpOnly cookie system (services/gateway/src/routes/auth.ts),
 * confirmed against the real backend before writing this file. Supabase
 * Auth (TODO.md Fase H, social login) is documented as a future decision
 * point, not implemented — this screen talks to /api/v1/auth/* only.
 */
export function AuthPage() {
  const { login, register } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [step, setStep] = useState<Step>("username")
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [checkingUsername, setCheckingUsername] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const ruleResults = evaluatePasswordPolicy(password)
  const allRulesPass = ruleResults.every((r) => r.passed)
  const passwordTouched = password.length > 0

  async function handleUsernameSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setCheckingUsername(true)
    try {
      const { exists } = await checkUserExists(username)
      setStep(exists ? "login" : "register")
    } catch (err) {
      setError(friendlyError(err).message)
    } finally {
      setCheckingUsername(false)
    }
  }

  async function handleLoginSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(username, password)
      const from = (location.state as { from?: Location })?.from?.pathname ?? "/dashboard"
      navigate(from, { replace: true })
    } catch (err) {
      setError(friendlyError(err).message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleRegisterSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await register(username, password)
      navigate("/dashboard", { replace: true })
    } catch (err) {
      setError(friendlyError(err).message)
    } finally {
      setSubmitting(false)
    }
  }

  function handleChangeUsername() {
    // Going back to edit the username invalidates whatever password was
    // typed against the previous one — never carry it across a switch
    // between the login/register branches.
    setPassword("")
    setError(null)
    setStep("username")
  }

  return (
    <AuthFormLayout title={step === "register" ? "Crear cuenta" : "Iniciar sesión"}>
      {step === "username" && (
        <form onSubmit={handleUsernameSubmit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <Input
            label="Usuario"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            minLength={3}
            maxLength={32}
            required
          />
          {error && (
            <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
              {error}
            </p>
          )}
          <Button type="submit" loading={checkingUsername}>
            Continuar
          </Button>
        </form>
      )}

      {step === "login" && (
        <form onSubmit={handleLoginSubmit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <UsernameSummary username={username} onChange={handleChangeUsername} />
          <Input
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />
          {error && (
            <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
              {error}
            </p>
          )}
          <Button type="submit" loading={submitting}>
            Entrar
          </Button>
        </form>
      )}

      {step === "register" && (
        <form onSubmit={handleRegisterSubmit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <UsernameSummary username={username} onChange={handleChangeUsername} />
          <Input
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
            required
          />

          {passwordTouched && (
            <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
              {PASSWORD_POLICY_RULES.map((rule) => {
                const passed = ruleResults.find((r) => r.ruleId === rule.id)?.passed ?? false
                return (
                  <li
                    key={rule.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-2)",
                      fontSize: "var(--text-xs)",
                      color: passed ? "var(--status-success)" : "var(--text-muted)",
                      transition: "color var(--duration-small) var(--ease-out)",
                    }}
                  >
                    <span aria-hidden>{passed ? "✓" : "○"}</span>
                    {rule.label}
                  </li>
                )
              })}
            </ul>
          )}

          {error && (
            <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
              {error}
            </p>
          )}

          <Button type="submit" loading={submitting} disabled={passwordTouched && !allRulesPass}>
            Crear cuenta
          </Button>
        </form>
      )}
    </AuthFormLayout>
  )
}

/** Shown once the username step is past: reminds the visitor which
 *  username they're continuing with and lets them go back, instead of
 *  silently locking it in behind the password field. */
function UsernameSummary({ username, onChange }: { username: string; onChange: () => void }) {
  return (
    <p style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", display: "flex", justifyContent: "space-between" }}>
      <span>
        Usuario: <strong style={{ color: "var(--text-primary)" }}>{username}</strong>
      </span>
      <button
        type="button"
        onClick={onChange}
        style={{ background: "none", border: "none", color: "var(--brand-primary)", cursor: "pointer", padding: 0, font: "inherit" }}
      >
        Cambiar
      </button>
    </p>
  )
}

function AuthFormLayout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", padding: "var(--space-4)" }}>
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          background: "var(--bg-surface)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-lg)",
          padding: "var(--space-8)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <h1 style={{ fontSize: "var(--text-2xl)", marginBottom: "var(--space-6)" }}>{title}</h1>
        {children}
      </div>
    </div>
  )
}
